/**
 * useBulkRecordActions Hook
 *
 * Auto-generates bulk record actions (delete, undelete, purge) from record_actions metadata.
 * Provides execution logic for sequential API calls with progress tracking.
 *
 * This hook is INTERNAL to MetadataTable - not exported publicly.
 */

import { useState, useMemo, useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { apiService } from '@/services/apiService'
import type { RecordActionsMetadata, LifecycleMetadata } from '@/services/metadataService'
import type { FailedItem } from '../components/BulkOperationResults'
import type { RecordActionKey } from '../components/RecordActionButton'

/**
 * Configuration for useBulkRecordActions hook
 */
export interface UseBulkRecordActionsOptions {
  /** API endpoint (e.g., '/offerings') */
  endpoint: string
  /** Record actions metadata from backend */
  recordActions: RecordActionsMetadata | undefined
  /** Optional lifecycle metadata (for state-aware filtering) */
  lifecycle?: LifecycleMetadata
  /** All data records currently loaded */
  data: Record<string, unknown>[]
  /** Currently selected row keys */
  selectedRowKeys: string[]
  /** Field name for row key (default: 'uid') */
  keyField?: string
  /** React Query key for cache invalidation */
  queryKey?: string[]
  /** Callback after successful bulk operation */
  onSuccess?: () => void
  /**
   * Whether viewing deleted records (trash mode).
   * Used to filter action visibility:
   * - Actions with requiresDeleted=true only shown when showDeleted=true
   * - Actions with requiresDeleted=false only shown when showDeleted=false
   * - Actions with requiresDeleted=undefined shown in both views
   */
  showDeleted?: boolean
}

/**
 * A single bulk record action with computed applicability
 */
export interface BulkRecordAction {
  /** Action key ('delete', 'undelete', 'purge') */
  key: RecordActionKey
  /** Button label */
  label: string
  /** Ant Design icon name */
  icon?: string
  /** Button type */
  buttonType?: 'primary' | 'default' | 'dashed' | 'link' | 'text'
  /** Whether this is a destructive action */
  danger?: boolean
  /** Number of selected items where action is valid */
  applicableCount: number
  /** Number of selected items where action is NOT valid */
  skippedCount: number
  /** Button is disabled if no applicable items */
  disabled: boolean
  /** Confirmation message template */
  confirmationMessage?: string | null
  /** Success message template */
  successMessage?: string
  /** Display order for consistent button positioning (lower = first). Lifecycle: 10-90, Record: 100+ */
  order?: number
}

/**
 * Progress state during bulk execution
 */
export interface BulkRecordProgress {
  /** Whether operation is in progress */
  isRunning: boolean
  /** Action being executed */
  operation: string
  /** Total items to process */
  total: number
  /** Completed successfully */
  completed: number
  /** Failed count */
  failed: number
}

/**
 * Results after bulk execution completes
 */
export interface BulkRecordResults {
  /** Whether to show results modal */
  visible: boolean
  /** Operation type */
  operation: string
  /** Total items attempted */
  total: number
  /** Items that succeeded */
  succeeded: number
  /** Items that failed */
  failed: number
  /** Details of failed items */
  failedItems: FailedItem[]
}

/**
 * Return type from useBulkRecordActions hook
 */
export interface UseBulkRecordActionsResult {
  /** Whether bulk record actions are available */
  enabled: boolean
  /** Auto-generated bulk actions from record_actions metadata */
  bulkActions: BulkRecordAction[]
  /** Execute a bulk action by key */
  execute: (actionKey: RecordActionKey) => Promise<void>
  /** Cancel an in-progress operation */
  cancel: () => void
  /** Current progress state */
  progress: BulkRecordProgress
  /** Results from last completed operation */
  results: BulkRecordResults
  /** Close the results modal */
  closeResults: () => void
}

/**
 * Check if a record is soft-deleted
 */
function isRecordDeleted(record: Record<string, unknown>): boolean {
  return record.deletedAt !== null && record.deletedAt !== undefined
}

/**
 * Hook for auto-generating bulk record actions from metadata
 *
 * @example
 * ```tsx
 * const { enabled, bulkActions, execute, progress, results } = useBulkRecordActions({
 *   endpoint: '/offerings',
 *   recordActions: metadata.record_actions,
 *   lifecycle: metadata.lifecycle,
 *   data,
 *   selectedRowKeys,
 *   onSuccess: () => refetch(),
 * })
 *
 * // Render buttons
 * {bulkActions.map(action => (
 *   <Button
 *     key={action.key}
 *     disabled={action.disabled}
 *     danger={action.danger}
 *     onClick={() => execute(action.key)}
 *   >
 *     {action.label} ({action.applicableCount})
 *   </Button>
 * ))}
 * ```
 */
export function useBulkRecordActions({
  endpoint,
  recordActions,
  lifecycle,
  data,
  selectedRowKeys,
  keyField = 'uid',
  queryKey,
  onSuccess,
  showDeleted = false,
}: UseBulkRecordActionsOptions): UseBulkRecordActionsResult {
  const queryClient = useQueryClient()
  const cancelRef = useRef(false)

  // Progress state
  const [progress, setProgress] = useState<BulkRecordProgress>({
    isRunning: false,
    operation: '',
    total: 0,
    completed: 0,
    failed: 0,
  })

  // Results state
  const [results, setResults] = useState<BulkRecordResults>({
    visible: false,
    operation: '',
    total: 0,
    succeeded: 0,
    failed: 0,
    failedItems: [],
  })

  // Create a lookup map for selected records
  const selectedRecordsMap = useMemo(() => {
    const map = new Map<string, Record<string, unknown>>()
    for (const record of data) {
      const key = String(record[keyField] ?? '')
      if (selectedRowKeys.includes(key)) {
        map.set(key, record)
      }
    }
    return map
  }, [data, selectedRowKeys, keyField])

  // Determine if bulk record actions are enabled
  const enabled = Boolean(
    recordActions && Object.keys(recordActions).length > 0
  )

  // Auto-generate bulk actions from record_actions metadata
  const bulkActions = useMemo<BulkRecordAction[]>(() => {
    if (!enabled || !recordActions) {
      return []
    }

    const stateField = lifecycle?.enabled ? lifecycle.stateField : undefined
    const actions: BulkRecordAction[] = []

    // Process each record action
    const actionKeys: RecordActionKey[] = ['delete', 'undelete', 'purge']

    for (const actionKey of actionKeys) {
      const actionConfig = recordActions[actionKey]
      if (!actionConfig) continue

      // Skip non-bulkable actions
      if (actionConfig.bulkable === false) continue

      // View-mode filtering: hide actions that don't apply to current view
      // - requiresDeleted=true (undelete, purge) -> only show in trash view
      // - requiresDeleted=false/undefined (delete) -> only show in normal view
      // This prevents showing irrelevant actions (e.g., Restore when not in trash)
      if (actionConfig.requiresDeleted === true && !showDeleted) continue
      if (actionConfig.requiresDeleted === false && showDeleted) continue

      // Compute applicability for selected items
      let applicableCount = 0
      let skippedCount = 0

      for (const recordKey of selectedRowKeys) {
        const record = selectedRecordsMap.get(recordKey)
        if (record) {
          const isDeleted = isRecordDeleted(record)
          const currentState = stateField
            ? (record[stateField] as string | undefined)
            : undefined

          // Check requiresDeleted flag
          let passesDeletedCheck = true
          if (actionConfig.requiresDeleted) {
            passesDeletedCheck = isDeleted
          } else {
            // Delete action should not apply to already-deleted records
            passesDeletedCheck = !isDeleted
          }

          // Check fromStates (lifecycle-aware filtering)
          let passesStateCheck = true
          if (
            actionConfig.fromStates &&
            actionConfig.fromStates.length > 0 &&
            currentState
          ) {
            passesStateCheck = actionConfig.fromStates.includes(currentState)
          }

          if (passesDeletedCheck && passesStateCheck) {
            applicableCount++
          } else {
            skippedCount++
          }
        }
      }

      actions.push({
        key: actionKey,
        label: actionConfig.label,
        icon: actionConfig.icon,
        buttonType: actionConfig.buttonType,
        danger: actionConfig.danger,
        applicableCount,
        skippedCount,
        disabled: applicableCount === 0,
        confirmationMessage: actionConfig.confirmationMessage,
        successMessage: actionConfig.successMessage,
        order: actionConfig.order,
      })
    }

    return actions
  }, [enabled, recordActions, lifecycle, selectedRowKeys, selectedRecordsMap, showDeleted])

  // Execute a bulk action
  const execute = useCallback(
    async (actionKey: RecordActionKey) => {
      if (!recordActions) return

      const action = bulkActions.find((a) => a.key === actionKey)
      if (!action || action.applicableCount === 0) return

      const actionConfig = recordActions[actionKey]
      if (!actionConfig) return

      const stateField = lifecycle?.enabled ? lifecycle.stateField : undefined
      cancelRef.current = false

      // Get applicable records
      const applicableRecords: Record<string, unknown>[] = []
      for (const recordKey of selectedRowKeys) {
        const record = selectedRecordsMap.get(recordKey)
        if (record) {
          const isDeleted = isRecordDeleted(record)
          const currentState = stateField
            ? (record[stateField] as string | undefined)
            : undefined

          // Check requiresDeleted flag
          let passesDeletedCheck = true
          if (actionConfig.requiresDeleted) {
            passesDeletedCheck = isDeleted
          } else {
            passesDeletedCheck = !isDeleted
          }

          // Check fromStates
          let passesStateCheck = true
          if (
            actionConfig.fromStates &&
            actionConfig.fromStates.length > 0 &&
            currentState
          ) {
            passesStateCheck = actionConfig.fromStates.includes(currentState)
          }

          if (passesDeletedCheck && passesStateCheck) {
            applicableRecords.push(record)
          }
        }
      }

      // Initialize progress
      setProgress({
        isRunning: true,
        operation: action.label,
        total: applicableRecords.length,
        completed: 0,
        failed: 0,
      })

      let completed = 0
      let failed = 0
      const failedItems: FailedItem[] = []

      // Execute sequentially
      for (const record of applicableRecords) {
        // Check for cancellation
        if (cancelRef.current) {
          break
        }

        const recordUid = String(record[keyField] ?? '')
        const recordName = String(record['name'] ?? record['label'] ?? recordUid)

        try {
          let response

          // Route to correct endpoint based on action
          if (actionKey === 'delete') {
            response = await apiService.delete(`${endpoint}/${recordUid}`)
          } else if (actionKey === 'undelete') {
            response = await apiService.post(`${endpoint}/${recordUid}/undelete`, {})
          } else if (actionKey === 'purge') {
            response = await apiService.delete(`${endpoint}/${recordUid}/purge`)
          }

          if (response?.success) {
            completed++
          } else {
            failed++
            failedItems.push({
              uid: recordUid,
              name: recordName,
              error: response?.errors?.[0]?.message ?? 'Unknown error',
            })
          }
        } catch (error) {
          failed++
          failedItems.push({
            uid: recordUid,
            name: recordName,
            error: error instanceof Error ? error.message : 'Unknown error',
          })
        }

        // Update progress
        setProgress((prev) => ({
          ...prev,
          completed,
          failed,
        }))
      }

      // Clear progress
      setProgress({
        isRunning: false,
        operation: '',
        total: 0,
        completed: 0,
        failed: 0,
      })

      // Show results
      setResults({
        visible: true,
        operation: action.label,
        total: applicableRecords.length,
        succeeded: completed,
        failed,
        failedItems,
      })

      // Invalidate queries to refetch data
      const key = queryKey || ['tableData', endpoint]
      queryClient.invalidateQueries({ queryKey: key })

      // Also invalidate record detail queries
      queryClient.invalidateQueries({
        queryKey: ['record', endpoint],
        exact: false,
      })

      // Call success callback if any items succeeded
      if (completed > 0) {
        onSuccess?.()
      }
    },
    [
      recordActions,
      bulkActions,
      lifecycle,
      selectedRowKeys,
      selectedRecordsMap,
      keyField,
      endpoint,
      queryKey,
      queryClient,
      onSuccess,
    ]
  )

  // Cancel operation
  const cancel = useCallback(() => {
    cancelRef.current = true
  }, [])

  // Close results modal
  const closeResults = useCallback(() => {
    setResults((prev) => ({ ...prev, visible: false }))
  }, [])

  return {
    enabled,
    bulkActions,
    execute,
    cancel,
    progress,
    results,
    closeResults,
  }
}
